import React, { useState, useEffect, useCallback, useRef } from 'react';
import ProfileAnnouncements from '../components/ProfileAnnouncements';
import CreditsBadge from '../components/CreditsBadge';
import { useRefreshable } from '../context/RefreshContext';
import { currencySymbolOf, USER_TYPES } from '../constants/designer';
import { tierOf, TierBadge, TierCheck, TierCaps } from '../components/TierMarks';
import { shopSocialHref, shopWebsiteHref, SHOP_SOCIALS } from '../constants/shop';
import { fetchShop } from '../firebase/shopService';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';
import UserMenu from '../components/UserMenu';
import PublicNav from '../components/PublicNav';
import NavBrand from '../components/NavBrand';
import PageSpinner from '../components/PageSpinner';
import AvatarLightbox from '../components/AvatarLightbox';
import HeaderSearch from '../components/HeaderSearch';
import NotificationBell from '../components/NotificationBell';
import MessagesButton from '../components/MessagesButton';
import { fetchDesigner } from '../firebase/designerService';
import { listGalleryItems } from '../firebase/galleryService';
import { ITEM_KINDS } from '../constants/gallery';
import { detectPlatform, contactHref } from '../utils/contactPlatforms';
import GalleryLightbox from '../components/GalleryLightbox';
import LikeButton, { useLike, LikeCount } from '../components/LikeButton';
import StyleList from '../components/StyleList';
import { fetchStats, SOCIAL_KINDS } from '../firebase/socialService';
import { useOpenChat } from '../hooks/useChat';
import useSeo from '../hooks/useSeo';
import { useChatUI } from '../context/ChatUIContext';
import './SearchPage.css';
import '../components/DesignerProfileHeader.css';
import '../components/GalleryManager.css';
import './DesignerProfile.css';
import './Dashboard.css';
import BackBar from '../components/BackBar';
import PersonIcon from '../components/PersonIcon';
import GuestGateModal from '../components/GuestGateModal';

function prettyHost(url) {
  const raw = String(url || '').trim();
  if (/^(tel|mailto|sms):/i.test(raw)) return raw.replace(/^(tel|mailto|sms):/i, '');
  const bare = raw
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '');
  const [host, ...rest] = bare.split('/');
  return (rest.length && rest[0] ? `${host}/${rest[0]}` : host).slice(0, 44);
}

// ── PUBLIC PROFILE ───────────────────────────────────────────────────────────
// What a visitor sees after clicking a search result: the account's public
// card, their photos, and (for a designer) their published 3D designs.
//
// ONE PAGE, TWO ROLES. `role` chooses which directory the card is read from and
// which facts describe it — a designer's experience and rate, or a shop's
// categories and delivery. Everything else on the page is identical by
// definition: the like button, the message button, the gallery, the lightbox,
// the view counts, the pull-to-refresh. A second page would have been ~350
// duplicated lines whose only job was to drift out of step with this one.
//
// Everything here is read through the PUBLIC surfaces — users/{uid} filtered on
// userType, plus the gallery collections in visitor mode, which the security
// rules already restrict to items marked visible. Nothing private (email,
// credits, plan) is touched, so this page cannot leak account data even by
// accident.
export default function DesignerProfile({ role = USER_TYPES.DESIGNER }) {
  const { uid } = useParams();
  const { t } = useT();
  const { user } = useAuth();
  const navigate = useNavigate();

  // Which role the page ended up RENDERING, which is not always the one the URL
  // asked for. A link into /designer/:uid that turns out to be a shop is
  // repaired here rather than shown as "not found": links to a profile are
  // minted in a dozen places, including notification rows written before shops
  // existed, and a wrong path is a link that could have worked. Costs one extra
  // read, and only when the first lookup came back empty.
  const [resolvedRole, setResolvedRole] = useState(role);
  const isShopPage = resolvedRole === USER_TYPES.SHOP;

  const [designer, setDesigner] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [tab, setTab] = useState(ITEM_KINDS.PHOTO);
  const [loading, setLoading] = useState(true);
  // WHOSE work is in `photos`/`designs` right now. Moving from one profile to
  // another reuses this component, so for a render or two the lists still hold
  // the previous designer's items while the URL already names the next one —
  // and `?photo=` must not be looked up in the wrong person's gallery.
  const [listsFor, setListsFor] = useState('');
  // Which tile the viewer is open on. -1 is closed.
  const [lightboxAt, setLightboxAt] = useState(-1);
  const [viewingAvatar, setViewingAvatar] = useState(false);
  // ── SIGNED OUT AND PRESSED SOMETHING THAT NEEDS AN ACCOUNT ────────────────
  // '' | 'like' | 'message' — which button opened the sign-up dialog, so it can
  // say why it appeared. See GuestGateModal.
  const [gate, setGate] = useState('');
  const openLikeGate = useCallback(() => setGate('like'), []);

  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  // `initial` separates the two callers. Opening the page shows the spinner and
  // may switch tabs or open a lightbox from ?photo=; a REFRESH must do neither
  // — blanking the page you are looking at, or yanking you to another tab,
  // is not what pulling down asks for.
  const load = useCallback(async ({ force = false, initial = false } = {}) => {
    if (initial) setLoading(true);
    try {
      const wantShop = role === USER_TYPES.SHOP;
      // `ownerView: false` — the visitor listing, which the rules and the
      // composite index both scope to visible items only.
      const [primary, ph, de] = await Promise.all([
        wantShop ? fetchShop(uid, { force }) : fetchDesigner(uid, { force }),
        listGalleryItems(ITEM_KINDS.PHOTO, uid, false).catch(() => []),
        // A shop publishes products, not 3D scenes — there is no second tab to
        // fill, so the query is skipped rather than run and thrown away.
        wantShop ? [] : listGalleryItems(ITEM_KINDS.DESIGN, uid, false).catch(() => []),
      ]);

      // The URL named the wrong role. Both directories memoise their misses, so
      // this second lookup happens once per uid per session at most.
      let card = primary;
      let actual = role;
      if (!card) {
        card = wantShop ? await fetchDesigner(uid, { force }) : await fetchShop(uid, { force });
        if (card) actual = wantShop ? USER_TYPES.DESIGNER : USER_TYPES.SHOP;
      }

      if (!alive.current) return;
      setResolvedRole(actual);
      setDesigner(card);
      setPhotos(ph);
      setDesigns(de);
      // These lists are now this uid's — see the opener effect below.
      setListsFor(uid);
      if (!initial) return;

      // Open on whichever tab actually has something in it.
      if (!ph.length && de.length) setTab(ITEM_KINDS.DESIGN);
    } finally {
      if (alive.current && initial) setLoading(false);
    }
    // `role`, the PROP, not the resolved role — otherwise repairing a mistyped
    // link would rebuild this callback and re-run the load it just finished.
  }, [uid, role]);

  useEffect(() => { load({ initial: true }); }, [load]);

  // ── ?photo= / ?design= OPENS THAT ITEM ────────────────────────────────────
  // A notification promises to open the picture it is about rather than drop
  // the reader on a grid to hunt for it. `?photo=` arrives from "liked your
  // photo" and from "replied to your comment"; `?design=` makes the same
  // promise for a 3D design, which now carries a thread of its own.
  //
  // AN EFFECT, NOT PART OF `load`. This was read once, inside the initial load,
  // which is only reached when the page MOUNTS. A notification tapped while
  // this very page is already open — a reply on a photo belonging to the
  // designer you are looking at — changes the query string and nothing else:
  // the route does not remount, the load does not re-run, and the tap appeared
  // to do nothing whatsoever.
  //
  // Keyed on the NAVIGATION rather than on the parameter's value. Closing the
  // lightbox leaves `?photo=` in the URL, so a guard on the value would refuse
  // to reopen the same picture when its notification is tapped again — while
  // no guard at all would spring it back open on every pull to refresh.
  const { key: navKey } = useLocation();
  const opened = useRef('');
  useEffect(() => {
    // Nothing to look the id up in until the lists belong to the uid the URL
    // names. Marking the navigation handled before then would swallow it.
    if (listsFor !== uid) return;
    if (opened.current === navKey) return;
    opened.current = navKey;

    const q = new URLSearchParams(window.location.search);
    const wantedPhoto = q.get('photo');
    if (wantedPhoto) {
      const at = photos.findIndex(p => p.id === wantedPhoto);
      if (at >= 0) { setTab(ITEM_KINDS.PHOTO); setLightboxAt(at); return; }
    }
    const wantedDesign = q.get('design');
    if (wantedDesign) {
      const at = designs.findIndex(d => d.id === wantedDesign);
      if (at >= 0) { setTab(ITEM_KINDS.DESIGN); setLightboxAt(at); }
    }
  }, [navKey, uid, listsFor, photos, designs]);

  // The like count on this page is the single most likely thing someone pulls
  // to refresh for, and it lives on the cached designer card — hence `force`.
  useRefreshable(() => load({ force: true }));

  const d = designer || {};
  // A designer card carries `fullName`, a shop card `name`. One local so every
  // consumer below — the heading, the avatar viewer, the chat hand-off — reads
  // the same value whichever page this is.
  const displayName = d.fullName || d.name || '';

  // ── THE PAID-TIER TREATMENT ───────────────────────────────────────────────
  // Read from the card's `plan` field, which designerService already exposes —
  // no extra fetch, and `tierOf` falls back to free (so, undecorated) for a card
  // saved before plans existed rather than throwing on the missing field.
  //
  // BOTH ROLES. Shops used to be gated out here because their directory card
  // did not carry a plan at all; it does now, so the same badge, ring, tick and
  // chips a paid designer wears are what a paid shop wears too. Which of the
  // two ladders to read is passed in — 'silver' means silver on either, but
  // they are separate tables and a shop has no bronze.
  const tier = tierOf(d, isShopPage);

  // One hook feeding two widely separated pieces of UI: the count under the
  // name and the button under the contacts. `designer` is null on first render,
  // so the real count arrives later — the hook re-syncs when it does.
  // A visitor without an account gets the dialog rather than a trip to /login —
  // they are in the middle of reading this profile, which is why they reached
  // for the button in the first place.
  const like = useLike(uid, designer?.likeCount || 0, { onAnonymous: openLikeGate });

  // Opening a conversation is one document get (the id is derived from the two
  // uids), then a hand-off to /messages — the thread's own listener lives there,
  // so this page never holds one.
  const { start: startChatWith, busy: chatBusy } = useOpenChat();
  const { openMessages } = useChatUI();
  async function startChat() {
    const chatId = await startChatWith({
      uid,
      name: displayName,
      avatar: d.avatarUrl || '',
    });
    if (chatId) openMessages(chatId);
  }

  // ── ARRIVED FROM A SHARED CARD ────────────────────────────────────────────
  // A share opened in a chat lands here as ?photo=<id>&from=<chatId>, and the
  // navigation closes the chat overlay on the way (it is state, not a route).
  // So closing the picture used to leave the reader on a stranger's profile
  // with the conversation gone — on a phone, with no obvious way back to it.
  //
  // Closing the viewer reopens that conversation over this page. The parameter
  // is dropped at the same time, so a second close, a reload or anything
  // bookmarked does not spring the chat open again unasked.
  const closeViewer = useCallback(() => {
    setLightboxAt(-1);
    const from = new URLSearchParams(window.location.search).get('from');
    if (!from) return;
    navigate(`${window.location.pathname}`, { replace: true });
    openMessages(from);
  }, [navigate, openMessages]);

  // The one-line summary under the name. Location is common to both; the rest
  // is whatever that role is actually described by.
  //
  // THE FULL ADDRESS, not the search key. `location` is trimmed to "place,
  // country" so the search filter can group on it; what belongs on a profile
  // is the address the owner actually chose — a shop that says only "Yerevan,
  // Armenia" cannot be visited. Falls back for profiles saved before it was
  // kept. See LocationPicker.
  const placeLine = d.locationAddress || d.location || null;
  const facts = (isShopPage
    ? [
      placeLine,
      d.delivery ? t('shop_delivery') : null,
    ]
    : [
      placeLine,
      d.experienceYears != null && d.experienceYears !== ''
        ? t('ds_years_short').replace('{n}', d.experienceYears) : null,
      d.hourlyRate != null && d.hourlyRate !== ''
        ? `${d.hourlyRate} ${currencySymbolOf(d.currency)} ${t(`ds_per_${d.rateUnit || 'hour'}`)}`.trim() : null,
    ]).filter(Boolean);

  // A shop's website is a single string and its socials a keyed map, where a
  // designer has two arrays. Flattened to the same {href, label} rows so the
  // markup below stays one block rather than two.
  const shopLinks = isShopPage
    ? SHOP_SOCIALS
      .filter(s => String(d.socials?.[s.id] || '').trim())
      .map(s => ({
        key: s.id,
        href: shopSocialHref(s.id, d.socials[s.id]),
        label: s.free ? d.socials[s.id] : `${t(`shop_social_${s.id}`)} · ${String(d.socials[s.id]).replace(/^@+/, '')}`,
        // A phone number opens the dialer in the same tab; a profile page does
        // not replace the page the visitor is browsing.
        newTab: !s.free,
      }))
    : [];

  const items = tab === ITEM_KINDS.PHOTO ? photos : designs;

  // Static counts for the visible grid — one batched query per tab, no
  // listeners. Photos are keyed by their own id, designs by the project behind
  // them, which is where a design's counters actually live.
  // BOTH tabs are fetched, not just the visible one: each tab header shows the
  // total likes for its own contents, so switching tabs must not be the moment
  // those numbers first appear. Two batched queries for the whole profile.
  const [stats, setStats] = useState({});
  const photoIdKey = photos.map(p => p.id).filter(Boolean).join(',');
  const designIdKey = designs.map(d => d.projectId).filter(Boolean).join(',');

  // `live`, not `alive` — the component now has an `alive` ref for the loaders,
  // and a local of the same name shadowing it inside these effects is exactly
  // the sort of thing that reads as correct and is not.
  useEffect(() => {
    if (!photoIdKey) return undefined;
    let live = true;
    fetchStats(SOCIAL_KINDS.PHOTO, photoIdKey.split(','))
      .then(s => { if (live) setStats(prev => ({ ...prev, ...s })); })
      .catch(() => {});
    return () => { live = false; };
  }, [photoIdKey]);

  useEffect(() => {
    if (!designIdKey) return undefined;
    let live = true;
    fetchStats(SOCIAL_KINDS.PROJECT, designIdKey.split(','))
      .then(s => { if (live) setStats(prev => ({ ...prev, ...s })); })
      .catch(() => {});
    return () => { live = false; };
  }, [designIdKey]);

  // The two effects above are keyed on the id LISTS, so they do not re-run when
  // a refresh returns the same items with different counts — which is precisely
  // the case a refresh exists for. Registered separately so the counts are
  // re-read whatever the ids did.
  useRefreshable(async () => {
    const jobs = [];
    if (photoIdKey) jobs.push(fetchStats(SOCIAL_KINDS.PHOTO, photoIdKey.split(',')));
    if (designIdKey) jobs.push(fetchStats(SOCIAL_KINDS.PROJECT, designIdKey.split(',')));
    if (!jobs.length) return;
    const results = await Promise.allSettled(jobs);
    if (!alive.current) return;
    const merged = results.reduce(
      (acc, r) => (r.status === 'fulfilled' ? { ...acc, ...r.value } : acc), {});
    setStats(prev => ({ ...prev, ...merged }));
  });

  // A photo's counters are keyed by its own id; a design's by the project
  // behind it, which is where the share page increments them.

  // Keeps the STATICALLY fetched counts honest after a like made in the
  // lightbox. Cheaper and calmer than re-querying: the delta is already known,
  // and the next real fetch brings the authoritative number anyway.
  const bumpLikes = (id, delta) => setStats(prev => (prev[id] ? {
    ...prev,
    [id]: { ...prev[id], likesCount: Math.max(0, (prev[id].likesCount || 0) + delta) },
  } : prev));

  const keyOf = (it, forTab) => (forTab === ITEM_KINDS.PHOTO ? it.id : it.projectId);
  const totalLikes = (rows, forTab) =>
    rows.reduce((sum, it) => sum + (stats[keyOf(it, forTab)]?.likesCount || 0), 0);

  // ── THIS PROFILE, BY NAME ─────────────────────────────────────────────────
  // Written from the row that has already loaded rather than from the URL, so
  // the title says who the page is about. Nothing is written until the name
  // arrives — useSeo skips a call with an empty title — and the canonical is
  // left to default to the current path, which is what a dynamic route wants.
  //
  // The description is assembled only from fields the profile actually
  // published: no invented specialism, no filler. If a designer wrote no bio
  // the sentence is shorter, which is the honest outcome.
  //
  // FIELD NAMES TAKEN FROM THE CARDS, not guessed: a designer card carries
  // `fullName`, a shop card `name`, and neither has a `bio` or a `city` — the
  // free text is `description` and the place is `location` (see toCard in
  // designerService.js and shopService.js). `displayName` above already
  // reconciles the first pair, so this reads that rather than repeating it.
  const seoRole = resolvedRole === USER_TYPES.SHOP ? 'Furniture shop' : 'Interior designer';
  useSeo({
    title: displayName ? `${displayName} — ${seoRole} | Tunify` : '',
    description: displayName
      ? [
        `${seoRole} ${displayName}${d.location ? ` in ${d.location}` : ''} on Tunify.`,
        d.description ? String(d.description).replace(/\s+/g, ' ').slice(0, 110) : '',
        'See their work and get in touch.',
      ].filter(Boolean).join(' ')
      : '',
    image: d.avatarUrl || undefined,
  });

  return (
    <div className="dash-root">
      <header className="dash-nav">
        <NavBrand />
        <div className="dash-nav-right">
          <HeaderSearch />
          <NotificationBell />
          <CreditsBadge />
          <MessagesButton />
          {user ? <UserMenu /> : <PublicNav />}
        </div>
      </header>

      <BackBar width={1240} />

      <main className="dash-main dp-main">
        {loading ? (
          <PageSpinner />
        ) : !designer ? (
          <p className="sp-empty">{isShopPage ? t('shop_not_found') : t('dp_not_found')}</p>
        ) : (
          <>
            {/* The account's own banner, above everything — designers and shops
                alike. Renders nothing on a plan without the feature, so no
                gate is needed here beyond telling it which ladder to read. */}
            <ProfileAnnouncements uid={d.uid || d.id} plan={d} isShop={isShopPage} />

            {/* Two tier classes, not one: `--tier` carries the structure every
                paid tier shares, `--bronze|silver|gold` only the metal. */}
            <header className={`dph dp-header${tier ? ` dph--tier dph--${tier.id}` : ''}`}>
              {/* Viewer only — a visitor can open the picture full size but has
                  nothing to change about it, so no `editable`. */}
              <button
                type="button"
                className={`dph-avatar dph-avatar--btn${tier ? ' dph-avatar--tier' : ''}`}
                onClick={() => setViewingAvatar(true)}
                aria-label={t('avl_open')}
                title={t('avl_open')}
              >
                {d.avatarUrl ? <img src={d.avatarUrl} alt="" /> : <PersonIcon />}
                <TierCheck tier={tier} />
              </button>

              {viewingAvatar && (
                <AvatarLightbox
                  src={d.avatarUrl || ''}
                  name={displayName}
                  onClose={() => setViewingAvatar(false)}
                />
              )}

              {/* Identity — name with its like count under it. Split out of
                  .dph-body so it can sit BESIDE the avatar while everything
                  below (facts, bio, chips, contacts) runs full width. */}
              <div className="dph-ident">
                {/* The badge sits ON the name line rather than under it: it is
                    an adjective about this person, and a row of its own reads
                    as a separate fact. Wrapping is allowed so a long name and a
                    translated badge never squeeze each other. */}
                <div className="dph-name-row">
                  <h1 className="dph-name">{displayName}</h1>
                  <TierBadge tier={tier} />
                </div>
                <div className="dp-like-count"><LikeCount {...like} /></div>
              </div>

              <div className="dph-body">
                {facts.length > 0 && (
                  <p className="dph-facts">
                    {facts.map((f, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && <span className="dph-dot" aria-hidden="true">·</span>}{f}
                      </React.Fragment>
                    ))}
                  </p>
                )}
                {/* What the tier actually buys, above the bio — it qualifies
                    the facts line it follows. Kept clear of the style chips
                    further down: those say what this designer works in, these
                    say what their plan puts them in line for, and one
                    undifferentiated run of pills would blur the two. */}
                <TierCaps tier={tier} isShop={isShopPage} />

                {d.description && <p className="dph-bio">{d.description}</p>}

                {/* The same capped chip row for both roles — a designer's
                    styles, a shop's categories. Only the i18n prefix differs. */}
                {isShopPage
                  ? d.categories?.length > 0 && (
                    <StyleList styles={d.categories} prefix="shop_cat_" moreLabel="shop_categories" />
                  )
                  : d.styles?.length > 0 && <StyleList styles={d.styles} />}

                <div className="dph-groups">
                  {/* ── SHOP: storefront and socials ── */}
                  {isShopPage && d.website && (
                    <div className="dph-group">
                      <span className="dph-group-label">{t('shop_website')}</span>
                      <ul className="dph-links">
                        <li>
                          <a
                            href={shopWebsiteHref(d.website)}
                            target="_blank" rel="noopener noreferrer nofollow"
                            className="dph-link"
                          >{prettyHost(d.website)}</a>
                        </li>
                      </ul>
                    </div>
                  )}
                  {isShopPage && shopLinks.length > 0 && (
                    <div className="dph-group">
                      <span className="dph-group-label">{t('shop_socials')}</span>
                      <ul className="dph-links">
                        {shopLinks.map(l => (
                          <li key={l.key}>
                            <a
                              href={l.href}
                              target={l.newTab ? '_blank' : undefined}
                              rel="noopener noreferrer nofollow"
                              className="dph-link"
                            >{l.label}</a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!isShopPage && d.websites?.length > 0 && (
                    <div className="dph-group">
                      <span className="dph-group-label">{t('ds_websites')}</span>
                      <ul className="dph-links">
                        {d.websites.map(w => (
                          <li key={w}>
                            <a href={w} target="_blank" rel="noopener noreferrer nofollow" className="dph-link">
                              {prettyHost(w)}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {!isShopPage && d.contacts?.length > 0 && (
                    <div className="dph-group">
                      <span className="dph-group-label">{t('ds_contacts')}</span>
                      <ul className="dph-links">
                        {d.contacts.map((c, i) => {
                          const p = detectPlatform(c.value, c.type);
                          const label = p.isKnown
                            ? (p.handle && p.key !== 'phone' && p.key !== 'email'
                                ? `${p.name} · ${p.handle}` : p.name || p.handle)
                            : prettyHost(c.value);
                          return (
                            <li key={`c${i}`}>
                              <a
                                href={contactHref(c.value, c.type)}
                                target={p.key === 'phone' || p.key === 'email' ? undefined : '_blank'}
                                rel="noopener noreferrer nofollow"
                                className="dph-link"
                              >{p.key === 'phone' ? c.value : label}</a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Last thing in the text column, under the contacts — the
                    calls to action once you have read who this person is.
                    Message sits beside Like: liking is a compliment, messaging
                    is the thing a marketplace actually exists for. */}
                {/* The tier fill reaches this button from `.dph--tier` on the
                    header, not from a prop: LikeButton is used on cards and in
                    the feed too, and it has no business knowing whose profile
                    it is standing on. */}
                <div className="dp-like">
                  <LikeButton like={like} />
                  {/* Hidden on your own profile — there is no conversation to
                      have with yourself, and openChat refuses it anyway.
                      SHOWN TO A VISITOR WITHOUT AN ACCOUNT: messaging is the
                      thing this page is trying to bring about, and hiding the
                      button until they sign up hid the reason to sign up.
                      Pressing it asks for the account instead of failing. */}
                  {user?.uid !== uid && (
                    <button
                      type="button"
                      className="dp-message"
                      onClick={user ? startChat : () => setGate('message')}
                      disabled={chatBusy}
                    >
                      <svg viewBox="0 0 24 24" width="17" height="17" fill="none"
                        stroke="currentColor" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 11.5a8.4 8.4 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.4 8.4 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8z" />
                      </svg>
                      {chatBusy ? '…' : t('chat_message')}
                    </button>
                  )}
                </div>
              </div>
            </header>

            {/* ── work ── */}
            <div className="dp-tabs" role="tablist">
              {[
                { key: ITEM_KINDS.PHOTO,
                  // Same grid, different word for what is in it: a designer
                  // shows photos of their work, a shop shows its products.
                  label: isShopPage ? t('shop_tab_products') : t('dg_tab_img'),
                  n: photos.length,
                  likes: totalLikes(photos, ITEM_KINDS.PHOTO) },
                // A shop publishes no 3D designs, so the tab is absent rather
                // than present and permanently empty.
                ...(isShopPage ? [] : [{
                  key: ITEM_KINDS.DESIGN, label: t('dg_tab_3d'), n: designs.length,
                  likes: totalLikes(designs, ITEM_KINDS.DESIGN),
                }]),
              ].map(x => (
                <button
                  key={x.key}
                  role="tab"
                  aria-selected={tab === x.key}
                  className={`dg-tab${tab === x.key ? ' dg-tab--on' : ''}`}
                  onClick={() => setTab(x.key)}
                >
                  {x.label} <span className="dp-tab-n">{x.n}</span>
                  {/* Total likes across everything in this tab — how the work
                      as a body is doing, which the per-tile numbers can't say.
                      Hidden at zero rather than shown as "0": an empty heart on
                      a new profile reads as a verdict. */}
                  {x.likes > 0 && (
                    <span className="dp-tab-likes" title={t('ps_likes')}>♥ {x.likes}</span>
                  )}
                </button>
              ))}
            </div>


            {items.length === 0 ? (
              <p className="sp-empty">{isShopPage ? t('shop_no_products') : t('dp_no_work')}</p>
            ) : (
              <div className="dp-grid">
                {items.map(it => (
                  <button
                    key={it.id}
                    type="button"
                    // `--listing` moves the views badge out of the bottom-left
                    // corner, which the product name now occupies.
                    className={`dp-tile${it.price != null ? ' dp-tile--listing' : ''}`}
                    // EVERYTHING OPENS IN THE LIGHTBOX, photos and 3D designs
                    // alike. A design used to open /share in a NEW TAB, which
                    // was the only thing available when the lightbox could show
                    // nothing but an image — it now embeds the real read-only
                    // scene (see GalleryLightbox), so the tab was throwing away
                    // the page the visitor was browsing to show them something
                    // this page can hold. It also left the design as the one
                    // item here with no likes and no comments beside it.
                    onClick={() => setLightboxAt(items.indexOf(it))}
                  >
                    <img src={it.thumbUrl} alt={it.description || ''} loading="lazy" />
                    {it.description && <span className="dp-cap">{it.description}</span>}
                    {tab === ITEM_KINDS.DESIGN && <span className="dp-3d">3D</span>}
                    {/* PRICE TOP-LEFT, NAME BOTTOM-LEFT — the two things a
                        product tile has to say before it is opened, in
                        opposite corners so neither crowds the other, and both
                        always visible rather than waiting on a hover a phone
                        cannot perform. Absent on anything with no price, which
                        is every photo a designer has published. */}
                    {it.price != null && (
                      <>
                        <span className="dp-price">
                          {it.price} {currencySymbolOf(it.currency)}
                        </span>
                        {it.title && <span className="dp-name">{it.title}</span>}
                      </>
                    )}
                    {/* Views, always visible in the bottom-left corner — the
                        same badge the owner sees on their own gallery, so a
                        tile reads identically from both sides. It used to
                        appear only on hover, which meant a visitor on a phone
                        never saw it at all. */}
                    {stats[keyOf(it, tab)] && (
                      <span className="gal-views dp-views" title={t('ps_views')}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2.2"
                          strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                        {stats[keyOf(it, tab)].viewsCount || 0}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      {/* The same viewer the owner's gallery uses — full-size fetch, the
          carousel for a multi-image post, likes and comments. The page once had
          its own cut-down overlay showing a stretched thumbnail. */}
      {lightboxAt >= 0 && items[lightboxAt] && (
        <GalleryLightbox
          item={items[lightboxAt]}
          kind={tab}
          onClose={closeViewer}
          onLikeChange={bumpLikes}
        />
      )}

      {/* Sign up over the page rather than instead of it — the button that
          opened this is still behind the dialog, and works the moment it
          closes. */}
      {gate && (
        <GuestGateModal
          reason={gate}
          name={displayName}
          onClose={() => setGate('')}
        />
      )}
    </div>
  );
}
